import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { message, Spin, Typography, Button, Input } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import 'react-quill-new/dist/quill.snow.css';
import {
  knowledgeBaseService,
  KnowledgeBase,
  KnowledgeBaseComment,
  getKnowledgeBaseErrorStatus
} from '../../services/knowledgeBase/knowledgeBaseService';
import VideoPlayer from '../../components/knowledgeBase/VideoPlayer';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import { resolveUploadAssetUrl } from '../../utils/resolveUploadAssetUrl';
import '../MemberManagement.css';
import '../../styles/resourceManagement.css';
import '../../styles/literature-lims.css';
import '../../styles/literature-stitch.css';

const { Paragraph } = Typography;

const statusLabel = (status: string) => {
  const m: Record<string, string> = { draft: '草稿', published: '已发布', archived: '已归档' };
  return m[status] || status;
};

const kbStatusPill = (status: string) => {
  const cls =
    status === 'published'
      ? 'rm-kb-pill rm-kb-pill--published'
      : status === 'archived'
        ? 'rm-kb-pill rm-kb-pill--archived'
        : 'rm-kb-pill rm-kb-pill--draft';
  return <span className={cls}>{statusLabel(status)}</span>;
};

const KnowledgeBaseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBase | null>(null);
  /** 首屏：仅概览未就绪 */
  const [loading, setLoading] = useState(true);
  /** 第二步：正文与评论加载中 */
  const [contentLoading, setContentLoading] = useState(true);
  const [contentLoadError, setContentLoadError] = useState<string | null>(null);
  const [comments, setComments] = useState<KnowledgeBaseComment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setContentLoading(true);
      setContentLoadError(null);
      setComments([]);
      setKnowledgeBase(null);

      try {
        const overview = await knowledgeBaseService.getOverview(id);
        if (cancelled) return;
        setKnowledgeBase(overview);
        setLoading(false);

        const [bodyResult, commentResult] = await Promise.allSettled([
          knowledgeBaseService.getBody(id),
          knowledgeBaseService.getComments(id)
        ]);
        if (cancelled) return;

        if (bodyResult.status === 'fulfilled') {
          const { content, viewCount } = bodyResult.value;
          setKnowledgeBase((prev) =>
            prev ? { ...prev, content, viewCount } : prev
          );
        } else {
          const err = bodyResult.reason;
          const msg = err instanceof Error ? err.message : '正文加载失败';
          setContentLoadError(msg);
          message.error('正文加载失败，请稍后重试');
        }

        if (commentResult.status === 'fulfilled') {
          setComments(commentResult.value.comments);
        } else {
          console.error('获取评论失败:', commentResult.reason);
        }
      } catch (error: unknown) {
        if (cancelled) return;
        const status = getKnowledgeBaseErrorStatus(error);
        const fallback = error instanceof Error ? error.message : '获取文档详情失败';
        if (status === 403) {
          message.error(
            '您暂无权限查看该文档（私密或受限访问）。请联系文档作者或管理员为您开通访问权限；仪器使用说明宜选用「公开」知识库文档以便全体成员阅读。'
          );
        } else if (status === 404) {
          message.error('文档不存在或已被删除。');
        } else {
          message.error(fallback);
        }
        navigate('/knowledge-base');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setContentLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('确定要删除该文档吗？')) return;
    try {
      await knowledgeBaseService.delete(id);
      message.success('删除成功');
      navigate('/knowledge-base');
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  const handleLike = async () => {
    if (!id) return;
    try {
      const result = await knowledgeBaseService.like(id);
      if (knowledgeBase) {
        setKnowledgeBase({ ...knowledgeBase, likeCount: result.likeCount });
      }
      message.success('点赞成功');
    } catch (error: any) {
      message.error(error.message || '点赞失败');
    }
  };

  const handleFavorite = async () => {
    if (!id) return;
    try {
      const result = await knowledgeBaseService.favorite(id);
      if (knowledgeBase) {
        setKnowledgeBase({ ...knowledgeBase, favoriteCount: result.favoriteCount });
      }
      message.success('收藏成功');
    } catch (error: any) {
      message.error(error.message || '收藏失败');
    }
  };

  const handleSubmitComment = async () => {
    if (!id || !commentText.trim()) return;
    setSubmittingComment(true);
    try {
      const newComment = await knowledgeBaseService.addComment(id, commentText);
      setComments([newComment, ...comments]);
      setCommentText('');
      message.success('评论成功');
    } catch (error: any) {
      message.error(error.message || '评论失败');
    } finally {
      setSubmittingComment(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container full-height members-lims kb-lims literature-lims-module d-flex align-items-center justify-content-center py-5">
        <Spin size="large" />
      </div>
    );
  }

  if (!knowledgeBase) {
    return (
      <div className="page-container full-height members-lims kb-lims literature-lims-module py-4">
        <nav className="mm-crumb" aria-label="breadcrumb">
          <HomeOutlined />
          <span>首页</span>
          <span className="mm-crumb-sep">/</span>
          <Link to="/knowledge-base" className="kb-crumb-link">
            知识库
          </Link>
        </nav>
        <div className="kb-detail-panel text-center py-5 text-muted">文档不存在或已删除</div>
      </div>
    );
  }

  const titleShort =
    knowledgeBase.title.length > 42 ? `${knowledgeBase.title.slice(0, 42)}…` : knowledgeBase.title;

  return (
    <div className="page-container full-height members-lims kb-lims literature-lims-module">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <Link to="/knowledge-base" className="kb-crumb-link">
          知识库
        </Link>
        <span className="mm-crumb-sep">/</span>
        <span aria-current="page">{titleShort}</span>
      </nav>

      <header className="mm-page-head">
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 w-100">
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <h1 className="mm-title kb-detail-title">{knowledgeBase.title}</h1>
            <p className="mm-subtitle mb-0">
              {knowledgeBase.summary || '查看正文、附件与评论，可使用下方操作进行互动或编辑。'}
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2 justify-content-end kb-detail-actions lit-stitch-attach-actions">
            <Button
              size="small"
              className="lit-stitch-btn-secondary"
              icon={<LimsNavIcon name="arrow_back" sizePx={18} />}
              onClick={() => navigate('/knowledge-base')}
            >
              返回列表
            </Button>
            <Button
              size="small"
              className="lit-stitch-btn-secondary"
              icon={<LimsNavIcon name="thumb_up" sizePx={18} />}
              onClick={handleLike}
            >
              点赞 ({knowledgeBase.likeCount})
            </Button>
            <Button
              size="small"
              className="lit-stitch-btn-secondary"
              icon={<LimsNavIcon name="star" sizePx={18} />}
              onClick={handleFavorite}
            >
              收藏 ({knowledgeBase.favoriteCount})
            </Button>
            <Button
              type="primary"
              size="small"
              className="lit-stitch-btn-primary"
              icon={<LimsNavIcon name="edit" sizePx={18} />}
              onClick={() => navigate(`/knowledge-base/${id}/edit`)}
            >
              编辑
            </Button>
            <Button
              danger
              size="small"
              className="lit-stitch-btn-secondary"
              icon={<LimsNavIcon name="delete_outline" sizePx={18} />}
              onClick={handleDelete}
            >
              删除
            </Button>
          </div>
        </div>
      </header>

      <div className="kb-detail-panel">
        <dl className="kb-meta-grid">
          <div>
            <dt>状态</dt>
            <dd>{kbStatusPill(knowledgeBase.status)}</dd>
          </div>
          <div>
            <dt>分类</dt>
            <dd>
              <span className="kb-meta-chip">{knowledgeBase.category || '未分类'}</span>
            </dd>
          </div>
          <div className="kb-meta-span-2">
            <dt>标签</dt>
            <dd>
              <div className="kb-tag-row">
                {knowledgeBase.tags?.length ? (
                  knowledgeBase.tags.map((tag) => (
                    <span key={tag} className="kb-tag">
                      {tag}
                    </span>
                  ))
                ) : (
                  <span className="text-muted small">—</span>
                )}
              </div>
            </dd>
          </div>
          <div>
            <dt>创建者</dt>
            <dd>{knowledgeBase.createdBy?.username ?? '—'}</dd>
          </div>
          <div>
            <dt>阅读量</dt>
            <dd style={{ fontVariantNumeric: 'tabular-nums' }}>{knowledgeBase.viewCount}</dd>
          </div>
          <div>
            <dt>创建时间</dt>
            <dd className="small">{new Date(knowledgeBase.createdAt).toLocaleString('zh-CN')}</dd>
          </div>
          <div>
            <dt>更新时间</dt>
            <dd className="small">{new Date(knowledgeBase.updatedAt).toLocaleString('zh-CN')}</dd>
          </div>
        </dl>

        {knowledgeBase.summary && (
          <section className="kb-detail-section">
            <h2 className="kb-section-title">摘要</h2>
            <Paragraph className="mb-0">{knowledgeBase.summary}</Paragraph>
          </section>
        )}

        {knowledgeBase.video && knowledgeBase.video.key && (
          <section className="kb-detail-section">
            <h2 className="kb-section-title">视频</h2>
            <VideoPlayer video={knowledgeBase.video} />
          </section>
        )}

        <section className="kb-detail-section">
          <h2 className="kb-section-title">正文</h2>
          {contentLoading ? (
            <div className="d-flex align-items-center gap-2 py-4 text-muted">
              <Spin />
              <span>加载正文与评论…</span>
            </div>
          ) : contentLoadError ? (
            <p className="text-danger mb-0">{contentLoadError}</p>
          ) : (
            <div
              className="ql-editor kb-quill-body"
              dangerouslySetInnerHTML={{ __html: knowledgeBase.content || '' }}
            />
          )}
        </section>

        {knowledgeBase.attachments && knowledgeBase.attachments.length > 0 && (
          <section className="kb-detail-section">
            <h2 className="kb-section-title">附件</h2>
            <ul className="kb-attach-list mb-0">
              {knowledgeBase.attachments.map((attachment) => (
                <li key={attachment.url}>
                  <a
                    href={resolveUploadAssetUrl(attachment.url) || attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {attachment.name}
                  </a>
                  <span className="text-muted small ms-2">({(attachment.size / 1024).toFixed(2)} KB)</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="kb-detail-section kb-comments">
          <h2 className="kb-section-title">评论 ({comments.length})</h2>
          <div className="mb-3">
            <Input.TextArea
              rows={4}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="写下你的评论…"
            />
            <Button
              type="primary"
              size="small"
              className="lit-stitch-btn-primary mt-2"
              icon={<LimsNavIcon name="send" sizePx={18} />}
              onClick={handleSubmitComment}
              disabled={submittingComment || !commentText.trim()}
            >
              发表评论
            </Button>
          </div>

          {contentLoading ? (
            <div className="text-muted small py-3">加载评论…</div>
          ) : (
            <ul className="kb-comment-list mb-0">
              {comments.map((comment) => (
                <li key={comment._id} className="kb-comment-item">
                  <div className="kb-comment-avatar" aria-hidden>
                    {(comment.userId?.username || 'U').charAt(0)}
                  </div>
                  <div>
                    <div className="kb-comment-head">
                      <span className="fw-semibold">{comment.userId?.username || '用户'}</span>
                      <span className="text-muted small ms-2">
                        {new Date(comment.createdAt).toLocaleString('zh-CN')}
                      </span>
                    </div>
                    <p className="kb-comment-body mb-0">{comment.content}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default KnowledgeBaseDetail;
